import { supabase } from '../lib/supabase';

// Email notification service for sending automated emails
export const emailNotificationService = {
  // Send quiz result notification
  async sendQuizResultNotification(userId, quizData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile for email
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('email, full_name')?.eq('id', userId)?.single();

      if (profileError) throw profileError;

      // Create email notification record
      const { data: notification, error: notificationError } = await supabase?.from('email_notifications')?.insert({
          user_id: userId,
          notification_type: 'quiz_result',
          recipient_email: profile?.email,
          subject: `Résultats de votre Quiz - ${quizData?.quiz_title}`,
          content: {
            quiz_title: quizData?.quiz_title,
            score: quizData?.score,
            total_questions: quizData?.total_questions,
            correct_answers: quizData?.correct_answers,
            message: quizData?.score >= 80 
              ? 'Félicitations! Vous avez obtenu un excellent score.' 
              : quizData?.score >= 60 
              ? 'Bon travail! Continuez à vous améliorer.' :'Continuez vos efforts, vous progressez!'
          },
          status: 'pending'
        })?.select()?.single();

      if (notificationError) throw notificationError;

      // Call Edge Function to send email
      let emailResult, emailError;
      try {
        const res = await supabase?.functions?.invoke('send-email-notification', {
          body: {
            recipientEmail: profile?.email,
            notificationType: 'quiz_result',
            subject: `Résultats de votre Quiz - ${quizData?.quiz_title}`,
            content: {
              quiz_title: quizData?.quiz_title,
              score: quizData?.score,
              total_questions: quizData?.total_questions,
              correct_answers: quizData?.correct_answers,
              message: quizData?.score >= 80 
                ? 'Félicitations! Vous avez obtenu un excellent score.' 
                : quizData?.score >= 60 
                ? 'Bon travail! Continuez à vous améliorer.' :'Continuez vos efforts, vous progressez!'
            }
          }
        });
        emailResult = res?.data;
        emailError = res?.error;
      } catch (invokeErr) {
        console.warn('[EMAIL] failed', invokeErr);
        await supabase?.from('email_notifications')?.update({ status: 'failed', error_message: invokeErr?.message })?.eq('id', notification?.id);
        return { success: false };
      }

      // Update notification status
      if (emailError) {
        console.warn('[EMAIL] failed', emailError);
        await supabase?.from('email_notifications')?.update({ 
            status: 'failed', 
            error_message: emailError?.message 
          })?.eq('id', notification?.id);
        return { success: false };
      } else {
        await supabase?.from('email_notifications')?.update({ 
            status: 'sent', 
            sent_at: new Date()?.toISOString() 
          })?.eq('id', notification?.id);
      }

      return { success: true, data: emailResult };
    } catch (error) {
      console.warn('[EMAIL] failed', error);
      return { success: false };
    }
  },

  // Send achievement notification
  async sendAchievementNotification(userId, achievementData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile for email
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('email, full_name')?.eq('id', userId)?.single();

      if (profileError) throw profileError;

      // Create email notification record
      const { data: notification, error: notificationError } = await supabase?.from('email_notifications')?.insert({
          user_id: userId,
          notification_type: 'achievement',
          recipient_email: profile?.email,
          subject: `Nouveau Badge Débloqué - ${achievementData?.achievement_title}`,
          content: {
            achievement_title: achievementData?.achievement_title,
            achievement_description: achievementData?.achievement_description,
            points: achievementData?.points,
            message: 'Continuez votre excellent travail!'
          },
          status: 'pending'
        })?.select()?.single();

      if (notificationError) throw notificationError;

      // Call Edge Function to send email
      let emailResult, emailError;
      try {
        const res = await supabase?.functions?.invoke('send-email-notification', {
          body: {
            recipientEmail: profile?.email,
            notificationType: 'achievement',
            subject: `Nouveau Badge Débloqué - ${achievementData?.achievement_title}`,
            content: {
              achievement_title: achievementData?.achievement_title,
              achievement_description: achievementData?.achievement_description,
              points: achievementData?.points,
              message: 'Continuez votre excellent travail!'
            }
          }
        });
        emailResult = res?.data;
        emailError = res?.error;
      } catch (invokeErr) {
        console.warn('[EMAIL] failed', invokeErr);
        await supabase?.from('email_notifications')?.update({ status: 'failed', error_message: invokeErr?.message })?.eq('id', notification?.id);
        return { success: false };
      }

      // Update notification status
      if (emailError) {
        console.warn('[EMAIL] failed', emailError);
        await supabase?.from('email_notifications')?.update({ 
            status: 'failed', 
            error_message: emailError?.message 
          })?.eq('id', notification?.id);
        return { success: false };
      } else {
        await supabase?.from('email_notifications')?.update({ 
            status: 'sent', 
            sent_at: new Date()?.toISOString() 
          })?.eq('id', notification?.id);
      }

      return { success: true, data: emailResult };
    } catch (error) {
      console.warn('[EMAIL] failed', error);
      return { success: false };
    }
  },

  // Send progress report notification
  async sendProgressReportNotification(userId, reportData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile for email
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('email, full_name')?.eq('id', userId)?.single();

      if (profileError) throw profileError;

      // Create email notification record
      const { data: notification, error: notificationError } = await supabase?.from('email_notifications')?.insert({
          user_id: userId,
          notification_type: 'progress_report',
          recipient_email: profile?.email,
          subject: `Rapport de Progression - ${reportData?.report_title}`,
          content: {
            report_title: reportData?.report_title,
            score: reportData?.score,
            progress_percentage: reportData?.progress_percentage,
            metrics: reportData?.metrics,
            message: 'Continuez votre excellent travail!'
          },
          status: 'pending'
        })?.select()?.single();

      if (notificationError) throw notificationError;

      // Call Edge Function to send email
      let emailResult, emailError;
      try {
        const res = await supabase?.functions?.invoke('send-email-notification', {
          body: {
            recipientEmail: profile?.email,
            notificationType: 'progress_report',
            subject: `Rapport de Progression - ${reportData?.report_title}`,
            content: {
              report_title: reportData?.report_title,
              score: reportData?.score,
              progress_percentage: reportData?.progress_percentage,
              metrics: reportData?.metrics,
              message: 'Continuez votre excellent travail!'
            }
          }
        });
        emailResult = res?.data;
        emailError = res?.error;
      } catch (invokeErr) {
        console.warn('[EMAIL] failed', invokeErr);
        await supabase?.from('email_notifications')?.update({ status: 'failed', error_message: invokeErr?.message })?.eq('id', notification?.id);
        return { success: false };
      }

      // Update notification status
      if (emailError) {
        console.warn('[EMAIL] failed', emailError);
        await supabase?.from('email_notifications')?.update({ 
            status: 'failed', 
            error_message: emailError?.message 
          })?.eq('id', notification?.id);
        return { success: false };
      } else {
        await supabase?.from('email_notifications')?.update({ 
            status: 'sent', 
            sent_at: new Date()?.toISOString() 
          })?.eq('id', notification?.id);
      }

      return { success: true, data: emailResult };
    } catch (error) {
      console.warn('[EMAIL] failed', error);
      return { success: false };
    }
  },

  // Save quiz result to database
  async saveQuizResult(quizResultData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('quiz_results')?.insert({
          user_id: user?.id,
          quiz_id: quizResultData?.quiz_id,
          quiz_title: quizResultData?.quiz_title,
          score: quizResultData?.score,
          total_questions: quizResultData?.total_questions,
          correct_answers: quizResultData?.correct_answers,
          answers: quizResultData?.answers
        })?.select()?.single();

      if (error) throw error;

      // Track field progress and award points (only once per field)
      if (quizResultData?.field_id) {
        await this.trackFieldProgress({
          user_id: user?.id,
          field_id: quizResultData?.field_id,
          score: quizResultData?.score
        });

        // Check and award badges based on progress milestones
        await this.checkAndAwardBadges(user?.id);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error saving quiz result:', error);
      return { success: false, error: error?.message };
    }
  },

  // Save achievement to database
  async saveAchievement(achievementData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('achievements')?.insert({
          user_id: user?.id,
          achievement_id: achievementData?.achievement_id,
          achievement_title: achievementData?.achievement_title,
          achievement_description: achievementData?.achievement_description,
          category: achievementData?.category,
          points: achievementData?.points,
          icon: achievementData?.icon
        })?.select()?.single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving achievement:', error);
      return { success: false, error: error?.message };
    }
  },

  // Save progress report to database
  async saveProgressReport(reportData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('progress_reports')?.insert({
          user_id: user?.id,
          report_title: reportData?.report_title,
          category: reportData?.category,
          score: reportData?.score,
          progress_percentage: reportData?.progress_percentage,
          metrics: reportData?.metrics
        })?.select()?.single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving progress report:', error);
      return { success: false, error: error?.message };
    }
  },

  // Get user's quiz results
  async getUserQuizResults(userId) {
    try {
      const { data, error } = await supabase?.from('quiz_results')?.select('*')?.eq('user_id', userId)?.order('completed_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching quiz results:', error);
      return { success: false, error: error?.message };
    }
  },

  // Get user's achievements
  async getUserAchievements(userId) {
    try {
      const { data, error } = await supabase?.from('achievements')?.select('*')?.eq('user_id', userId)?.order('earned_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching achievements:', error);
      return { success: false, error: error?.message };
    }
  },

  // Get user's progress reports
  async getUserProgressReports(userId) {
    try {
      const { data, error } = await supabase?.from('progress_reports')?.select('*')?.eq('user_id', userId)?.order('report_date', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching progress reports:', error);
      return { success: false, error: error?.message };
    }
  },

  // Get user's email notifications
  async getUserEmailNotifications(userId) {
    try {
      const { data, error } = await supabase?.from('email_notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching email notifications:', error);
      return { success: false, error: error?.message };
    }
  },

  // Track field progress and award points only once
  async trackFieldProgress({ user_id, field_id, score }) {
    try {
      const validated = score >= 70;

      // Check if progress already exists
      const { data: existing } = await supabase
        ?.from('field_progress')
        ?.select('*')
        ?.eq('user_id', user_id)
        ?.eq('field_id', field_id)
        ?.single();

      if (existing) {
        // Update existing progress (allow retry but no re-award)
        await supabase
          ?.from('field_progress')
          ?.update({
            quiz_completed: true,
            quiz_validated: validated,
            completed_at: new Date()?.toISOString()
          })
          ?.eq('user_id', user_id)
          ?.eq('field_id', field_id);

        return { success: true, points_awarded: 0, is_retry: true };
      } else {
        // First attempt: award points
        const completionPoints = 50;
        const validationPoints = validated ? 100 : 0;
        const totalPoints = completionPoints + validationPoints;

        // Insert new progress record
        await supabase
          ?.from('field_progress')
          ?.insert({
            user_id,
            field_id,
            quiz_completed: true,
            quiz_validated: validated,
            points_awarded: true,
            completed_at: new Date()?.toISOString()
          });

        // Award achievement points
        await supabase
          ?.from('achievements')
          ?.insert({
            user_id,
            achievement_id: `field_quiz_${field_id}`,
            achievement_title: validated ? 'Quiz Validé' : 'Quiz Complété',
            achievement_description: validated 
              ? `Score de ${score}% - Quiz validé avec succès`
              : `Quiz complété avec un score de ${score}%`,
            category: 'quiz_completion',
            points: totalPoints
          });

        return { success: true, points_awarded: totalPoints, is_retry: false };
      }
    } catch (error) {
      console.error('Error tracking field progress:', error);
      return { success: false, error: error?.message };
    }
  },

  // Check and award badges based on progress milestones
  async checkAndAwardBadges(user_id) {
    try {
      // Get user's field progress
      const { data: progress } = await supabase
        ?.from('field_progress')
        ?.select('*')
        ?.eq('user_id', user_id);

      if (!progress) return { success: true, badges_awarded: [] };

      const badgesAwarded = [];

      // Check for "Profil Identifié" badge (1 validated quiz)
      const validatedCount = progress?.filter(p => p?.quiz_validated)?.length;
      if (validatedCount >= 1) {
        const { data: existing } = await supabase
          ?.from('achievements')
          ?.select('id')
          ?.eq('user_id', user_id)
          ?.eq('achievement_id', 'profil-identifie')
          ?.single();

        if (!existing) {
          await supabase
            ?.from('achievements')
            ?.insert({
              user_id,
              achievement_id: 'profil-identifie',
              achievement_title: 'Profil Identifié',
              achievement_description: 'Vous avez validé votre premier quiz avec succès',
              category: 'milestone',
              points: 50,
              icon: 'Award'
            });
          badgesAwarded?.push('Profil Identifié');
        }
      }

      // Check for "Explorateur" badge (3 explored fields)
      const exploredCount = progress?.length;
      if (exploredCount >= 3) {
        const { data: existing } = await supabase
          ?.from('achievements')
          ?.select('id')
          ?.eq('user_id', user_id)
          ?.eq('achievement_id', 'explorateur')
          ?.single();

        if (!existing) {
          await supabase
            ?.from('achievements')
            ?.insert({
              user_id,
              achievement_id: 'explorateur',
              achievement_title: 'Explorateur',
              achievement_description: 'Vous avez exploré 3 filières différentes',
              category: 'exploration',
              points: 100,
              icon: 'Compass'
            });
          badgesAwarded?.push('Explorateur');
        }
      }

      return { success: true, badges_awarded: badgesAwarded };
    } catch (error) {
      console.error('Error checking and awarding badges:', error);
      return { success: false, error: error?.message };
    }
  }
};